import {
  calculateBudgetForProject,
  getRolesListForDeliverable,
} from "../../../../../planner/milestone/utils/budget.utils";
import { getFormattedExpenseDataForProject } from "./single-project-financials.utils";
import { isSomething } from "../../../../../../utils/utils";
import { ROLES_LIST } from "../../../../../planner/milestone/utils/budget.constants";
import { createRolesMap } from "../../../../../../utils/rateCard";

export const calculateProjectTotalFinancialsNew = (
  project,
  users,
  rateCards,
  companiesList
) => {
  if (!project || !users) return null;

  const id = project?.id;
  const deliverables = project?.deliverables;
  const resources = project?.resources;

  const { hours: totalBudgetedHours, value: totalBudgetCost } =
    calculateBudgetForProject(deliverables, resources);

  const projectRateCard = rateCards?.find(
    (rateCard) => rateCard?.id === project?.rateOption
  );

  const projectRolesMap = createRolesMap(projectRateCard);

  const matchedCompany = companiesList?.find(
    (c) => String(c.companyID) === String(project?.client)
  );
  const clientRateCardId = matchedCompany?.rateCard;

  const companyRateCard = rateCards?.find(
    (rateCard) => rateCard?.id === clientRateCardId
  );

  const companyRolesMap = createRolesMap(companyRateCard);

  const expenseData = getFormattedExpenseDataForProject(
    users,
    id,
    resources,
    projectRolesMap,
    companyRolesMap
  );

  const expenseDataWithRoleAndDeliverable = expenseData?.filter(
    (x) => isSomething(x?.role) && isSomething(x?.deliverable)
  );

  // ----------------------------
  // OTHER ACTUALS LOGIC
  // ----------------------------

  // Identify “Other Actuals” – expense entries that do not match a budgeted role for the deliverable.
  const otherActuals = expenseData?.filter((entry) => {
    const isRoleBudgetedForDeliverable = deliverables?.some(
      (deliverable) =>
        String(deliverable?.name) === String(entry?.deliverable) &&
        getRolesListForDeliverable(deliverable, resources).some(
          (budgetedRole) =>
            String(budgetedRole?.role).toLowerCase() ===
            String(entry?.role).toLowerCase()
        )
    );
    return !isRoleBudgetedForDeliverable;
  });

  // Overall summary for Other Actuals.
  const otherActualsSummary = otherActuals?.reduce(
    (totals, entry) => {
      const role = entry?.role || entry?.users?.role || "Unknown Role";
      // Use a default rate based on ROLES_LIST if needed.
      const rate =
        entry?.cost ||
        (entry?.role
          ? ROLES_LIST?.[entry.role]?.rate || 0
          : entry?.users?.rate || 0);

      const hours = entry?.hours || 0;
      const cost = entry?.cost || hours * rate;
      return {
        hours: totals.hours + hours,
        cost: totals.cost + cost,
      };
    },
    { hours: 0, cost: 0 }
  );

  // Expense summary for entries that match budgeted roles.
  const totalExpenseSummary = expenseDataWithRoleAndDeliverable?.reduce(
    (totals, entry) => {
      // Skip entries that are already classified as "Other Actuals".
      const isInOtherActuals = otherActuals?.some(
        (other) =>
          other.flowId === entry.flowId &&
          other.role === entry.role &&
          other.deliverable === entry.deliverable
      );
      if (isInOtherActuals) return totals;

      const hours = entry?.hours || 0;
      const cost = entry?.cost || 0;
      return {
        hours: totals.hours + hours,
        cost: totals.cost + cost,
      };
    },
    { hours: 0, cost: 0 }
  );

  // Combine expense totals for budgeted entries and Other Actuals.
  const combinedExpenseSummary = {
    hours:
      (totalExpenseSummary?.hours || 0) + (otherActualsSummary?.hours || 0),
    cost: (totalExpenseSummary?.cost || 0) + (otherActualsSummary?.cost || 0),
  };

  // Breakdown of Other Actuals by role.
  const otherActualsByRole = otherActuals?.reduce((acc, entry) => {
    const role =
      entry?.role ||
      entry?.users?.role ||
      "Unknown Role (not included in estimates & using default role rates)";
    const rate =
      entry?.cost ||
      (entry?.role
        ? ROLES_LIST?.[entry.role]?.rate || 0
        : entry?.users?.rate || 0);
    const hours = entry?.hours || 0;
    const cost = entry?.cost || hours * rate;
    if (!acc[role]) {
      acc[role] = { hours: 0, cost: 0 };
    }
    acc[role].hours += hours;
    acc[role].cost += cost;
    return acc;
  }, {});

  // ----------------------------
  // RETURN THE FINANCIALS OBJECT
  // ----------------------------
  return {
    budget: {
      hours: totalBudgetedHours,
      cost: totalBudgetCost,
    },
    expense: {
      // Provide top-level keys for backward compatibility.
      hours: combinedExpenseSummary.hours,
      cost: combinedExpenseSummary.cost,
      // Detailed breakdown.
      total: combinedExpenseSummary,
      otherActualsSummary,
      otherActualsBreakdown: otherActualsByRole,
      // Optionally include the raw expense data.
      originalExpenseData: expenseData,
      actualExpense: expenseData?.reduce(
        (totalCost, entry) => {
          const actualCost = (entry?.hours || 0)*(entry?.users?.cost || 0);
          return totalCost + actualCost;
        },
        0
      ),
    },
  };
};
